import * as React from 'react';
import { Dispatch } from 'react-redux';
import { Translate } from 'react-redux-i18n';

import { PageWrapper, StyledPage, PageInner, StyledIframe, Loader, htmlEditorStyles } from './styles';
import { HEADER_HEIGHT, PAGE_TITLE_HEIGHT, PAGE_PADDING } from '../styles';
import { StoreState } from '../../../reducers';
import { PAGE_BACKGROUND_CLICK_CLASS } from './Background';
import Background from '../../../containers/Editor/Page/Background';
import Content from '../../../containers/Editor/Page/Content';
import RichTextEditor from '../../../containers/Editor/RichTextEditor';
import MediaEditor from '../../../containers/Editor/MediaEditor';
import LinkEditor from '../../../containers/Editor/LinkEditor';
import Portal from '../../Portal';
import { resetActiveItem } from '../../../containers/Editor/actions';
import { EditorType, editedElementClassName } from '../../../containers/Editor/constants';
import { editableId, editorStylesId, editorFontsId, editorGoogleFontsId } from '../../../containers/Editor/utils/htmlEditor/constants';
import { createNotification } from '../../../containers/NotificationBar/actions';
import Utils from '../../../utils';
const svgSpritePath = require('../../../images/icons.svg');

interface PageProps {
	editorType: EditorType;
	page: number;
	width: number;
	height: number;
	zoom: number;
	html: string;
	iframeUrl: string;
	fontsPath: string;
	googleFontsPath: string;
	isHidden: boolean;
	isThumbnailsDisplayed: boolean;
	isEditable: boolean;
	editableTextFieldsIds: string[];
	editableMediaIds: string[];
	editableLinksIds: string[];
	isSomeEditableItemActive: boolean;
	editorContainer: Element | null;
	isTransparent: boolean;
	dispatch: Dispatch<StoreState>;
}

interface PageState {
	containerWidth: number;
	containerHeight: number;
	currentWidth: number;
	currentHeight: number;
	currentScale: number;
	isDisplayed: boolean; // is needed for initial render to prevent page jumping (when page sizes are not calculated)
}

declare global {
	interface Window {
		visualViewport: EventTarget;
	}
}

// Elements with this classname ignore click on page (it prevents reseting of active editable text or media)
export const IGNORE_PAGE_CLICK_CLASS = 'ignore-page-click';

const Page = ({
	editorType,
	page,
	width,
	height,
	zoom,
	html,
	iframeUrl,
	fontsPath,
	googleFontsPath,
	isHidden,
	isThumbnailsDisplayed,
	isEditable,
	editableTextFieldsIds,
	editableMediaIds,
	editableLinksIds,
	isSomeEditableItemActive,
	editorContainer,
	isTransparent,
	dispatch,
}: PageProps) => {
	const initialState = {
		containerWidth: 0,
		containerHeight: 0,
		currentWidth: width,
		currentHeight: height,
		currentScale: zoom,
		isDisplayed: false,
	};

	const [{
		containerWidth,
		containerHeight,
		currentWidth,
		currentHeight,
		currentScale,
		isDisplayed,
	}, setState] = React.useState<PageState>(initialState);
	const [isIframeDocumentReady, setIsIframeDocumentReady] = React.useState(false);

	const container = React.useRef<HTMLDivElement>(null);
	const iframe = React.useRef<HTMLIFrameElement>(null);

	const setPageSize = (): void => {
		const { offsetWidth } = container.current as HTMLDivElement;
		const offsetHeight = window.innerHeight - HEADER_HEIGHT - PAGE_TITLE_HEIGHT - PAGE_PADDING * 2;
		const isKeyboardAppeared = offsetWidth === containerWidth && offsetHeight < containerHeight;

		if (isKeyboardAppeared) {
			return;
		}

		let newWidth = offsetWidth;
		let newScale = newWidth / width;
		let newHeight = height * newScale;

		const isPageHigherThanContainer = newHeight > offsetHeight;

		if (isPageHigherThanContainer) {
			newHeight = offsetHeight;
			newScale = newHeight / height;
			newWidth = width * newScale;
		}

		setState({
			containerWidth: offsetWidth,
			containerHeight: offsetHeight,
			currentWidth: newWidth * zoom,
			currentHeight: newHeight * zoom,
			currentScale: newScale * zoom,
			isDisplayed: true,
		});
	};

	React.useEffect(() => {
		if (editorType === EditorType.HTML || isHidden) {
			return;
		}

		setPageSize();

		// Standard window 'resize' event isn't triggered in webview on iOS13, it should be listened on window.visualViewport
		if (window.visualViewport) {
			window.visualViewport.addEventListener('resize', setPageSize);
		} else {
			window.addEventListener('resize', setPageSize);
		}

		return () => {
			if (window.visualViewport) {
				window.visualViewport.removeEventListener('resize', setPageSize);
			} else {
				window.removeEventListener('resize', setPageSize);
			}
		};
	}, [width, height, containerWidth, containerHeight, zoom, isThumbnailsDisplayed, isHidden, editorType]);

	React.useEffect(() => {
		if (!isSomeEditableItemActive) {
			return;
		}

		const handleWindowClick = (e: MouseEvent | TouchEvent) => {
			const element = e.target as HTMLElement;
			const shouldIgnoreClick = !!element.closest(`.${IGNORE_PAGE_CLICK_CLASS}`);

			if (!shouldIgnoreClick) {
				dispatch(resetActiveItem());
			}
		};

		if (editorContainer) {
			editorContainer.addEventListener('mousedown', handleWindowClick);
		}

		if (iframe.current) {
			iframe.current.contentDocument!.addEventListener('mousedown', handleWindowClick);
		}

		return () => {
			if (editorContainer) {
				editorContainer.removeEventListener('mousedown', handleWindowClick);
			}

			if (iframe.current) {
				iframe.current.contentDocument!.removeEventListener('mousedown', handleWindowClick);
			}
		};
	}, [isSomeEditableItemActive]);

	React.useEffect(() => {
		if (editorType === EditorType.HTML) {
			setIsIframeDocumentReady(false);
		}

		if (container.current) {
			container.current.scrollIntoView();
		}
	}, [page]);

	React.useEffect(() => {
		if (editorType === EditorType.INDESIGN) {
			return;
		}

		// load fonts to main document to render them in text edit panel
		const addEditorFonts = () => {
			const addLinkElement = (id: string, href: string): void => {
				const linkElement = document.createElement('link');
				linkElement.id = id;
				linkElement.rel = 'stylesheet';
				linkElement.href = href;
				document.head.appendChild(linkElement);
			};

			if (fontsPath) {
				if (fontsPath) {
					addLinkElement(editorFontsId, iframeUrl.replace('index.html', fontsPath));
				}
			}

			if (googleFontsPath) {
				addLinkElement(editorGoogleFontsId, googleFontsPath);
			}
		};

		const removeEditorFonts = () => {
			const removeLinkElement = (id: string): void => {
				const linkElement = document.querySelector(`#${id}`);
				if (linkElement) {
					linkElement.parentNode!.removeChild(linkElement);
				}
			};

			removeLinkElement(editorFontsId);
			removeLinkElement(editorGoogleFontsId);
		};

		addEditorFonts();

		return () => removeEditorFonts();
	}, []);

	const handleIframeLoad = async () => {
		const addEditorStyles = (iframeDocument: Document) => {
			const editorStyleElement = iframeDocument.createElement('style');
			editorStyleElement.id = editorStylesId;
			editorStyleElement.innerHTML = htmlEditorStyles;
			iframeDocument.head.appendChild(editorStyleElement);
		};

		const addSvgSprite = async (iframeDocument: Document) => {
			const svgSprite = await Utils.promiseXHR('GET', svgSpritePath, undefined, undefined, undefined, undefined, undefined, true);
			const div = iframeDocument.createElement('div');
			div.innerHTML = svgSprite.responseText;
			iframeDocument.body.appendChild(div);
		};

		try {
			const iframeDocument = iframe.current!.contentDocument as Document;
			addEditorStyles(iframeDocument);
			iframeDocument.body.innerHTML = html;
			await addSvgSprite(iframeDocument);
		} catch (error) {
			dispatch(createNotification('error.parse_editor_data_error'));
		} finally {
			setIsIframeDocumentReady(true);
		}
	};

	const getParentContainer = (id: string): Element | null => {
		const selector = editorType === EditorType.INDESIGN
			? `#${id}`
			: `[${editableId}=${id}]`;
		const pageDocument = iframe.current ? iframe.current.contentDocument : document;

		if (!pageDocument) {
			return null;
		}

		return pageDocument.querySelector(selector);
	};

	const renderRichTextEditors = () => (
		<>
			{editableTextFieldsIds.map((id) => {
				// tslint:disable-next-line: max-line-length
				// We have to render <RichTextEditor /> in corresponding DOM nodes in <Background /> to keep all parents of text frames generated by inDesign (e.g., text frames inside groups or animated frames)
				const parentContainer = getParentContainer(id);
				return parentContainer && (
					<Portal key={id} parentNode={parentContainer}>
						<RichTextEditor
							page={page}
							id={id}
							pageScale={currentScale}
							isEdited={parentContainer.classList.contains(editedElementClassName)}
						/>
					</Portal>
				);
			})}
		</>
	);

	const renderMediaEditors = () => (
		<>
			{editableMediaIds.map((id) => {
				const parentContainer = getParentContainer(id);
				return parentContainer && (
					<Portal key={id} parentNode={parentContainer}>
						<MediaEditor
							page={page}
							id={id}
							pageScale={currentScale}
							parentContainer={parentContainer}
							iframeContainer={iframe.current}
						/>
					</Portal>
				);
			})}
		</>
	);

	const renderLinkEditors = () => (
		<>
			{editableLinksIds.map((id) => {
				const parentContainer = getParentContainer(id);
				return parentContainer && (
					<Portal key={id} parentNode={parentContainer}>
						<LinkEditor
							page={page}
							id={id}
							pageScale={currentScale}
						/>
					</Portal>
				);
			})}
		</>
	);

	if (editorType === EditorType.HTML) {
		return (
			<PageWrapper
				ref={container}
				hidden={isHidden}
				editorType={editorType}
				zoom={zoom}
			>
				{!isIframeDocumentReady &&
					<Loader>
						{Utils.getSvgIcon('iconset_loading')}
						<Translate value="messages.loading" />
					</Loader>
				}
				<StyledIframe
					ref={iframe}
					src={iframeUrl}
					isLoading={!isIframeDocumentReady}
					onLoad={handleIframeLoad}
				/>
				{isEditable && isIframeDocumentReady && (
					<>
						{renderRichTextEditors()}
						{renderMediaEditors()}
						{renderLinkEditors()}
					</>
				)}
			</PageWrapper>
		);
	}

	return (
		<Background
			isHidden={isHidden}
			editorContainer={editorContainer}
		>
			<PageWrapper
				ref={container}
				className={PAGE_BACKGROUND_CLICK_CLASS}
				editorType={editorType}
				zoom={zoom}
			>
				<StyledPage
					className={isTransparent ? PAGE_BACKGROUND_CLICK_CLASS : ''}
					width={isDisplayed ? currentWidth : 0}
					height={isDisplayed ? currentHeight : 0}
					isTransparent={isTransparent}
					isSomeEditableItemActive={isSomeEditableItemActive}
				>
					<PageInner scale={currentScale}>
						<Content />
						{isEditable &&
							<>
								{renderRichTextEditors()}
								{renderMediaEditors()}
								{renderLinkEditors()}
							</>
						}
					</PageInner>
				</StyledPage>
			</PageWrapper>
		</Background>
	);
};

export default Page;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/Page/index.tsx